/**
 * VisibilityStrategyPage — /visibility-strategy
 *
 * The free top-of-funnel lead magnet. Covers 4 failure points that keep
 * AI/ML candidates invisible to recruiters, with explicit routing into
 * the deeper CareerForge products for each failure point.
 *
 * TIER: Free for everyone, no login required.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import BuildIcon from "@mui/icons-material/Build";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  FAILURE_POINTS,
  HIRING_REALITY_STATS,
  HIRING_REALITY_TALKING_POINTS,
  WEEKLY_ACTION_ITEMS,
  CLOSING_NOTE,
  type FailurePoint,
} from "../data/visibilityStrategy";
import { L } from "../utils/localized";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// ---------------------------------------------------------------------------
// Failure point section
// ---------------------------------------------------------------------------

function FailurePointSection({ fp, index }: { fp: FailurePoint; index: number }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-account");

  return (
    <Box mb={8} id={fp.slug}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: "1.6rem",
          }}
        >
          {fp.icon}
        </Box>
        <Box>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.error.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("VisibilityStrategyPage.failurePointOfFour", { number: fp.number })}
          </Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {fp.title}
          </Typography>
        </Box>
      </Stack>

      {/* Problem */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.error.main, 0.04),
          border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
          borderRadius: 3,
          p: 3,
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <WarningIcon sx={{ color: theme.palette.error.dark, fontSize: 22, mt: "2px", flexShrink: 0 }} />
          <Box flex={1}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.error.dark, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 1 }}
            >
              {t("VisibilityStrategyPage.theProblemLabel")}
            </Typography>
            <Typography variant="h6" fontWeight={500} sx={{ mb: 1.5, lineHeight: 1.4 }}>
              {fp.problem.headline}
            </Typography>
            {fp.problem.stat && (
              <Typography variant="h2" fontWeight={900} sx={{ color: theme.palette.error.dark, letterSpacing: "-0.03em", lineHeight: 1, mb: 1.5 }}>
                {fp.problem.stat}
              </Typography>
            )}
            <Typography variant="body1" sx={{ lineHeight: 1.75, color: "text.primary" }}>
              {fp.problem.detail}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Solution */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.success.main, 0.04),
          border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
          borderRadius: 3,
          p: 3,
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <BuildIcon sx={{ color: theme.palette.success.dark, fontSize: 22, mt: "2px", flexShrink: 0 }} />
          <Box flex={1}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.success.dark, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 1 }}
            >
              {t("VisibilityStrategyPage.theSolutionLabel")}
            </Typography>
            <Typography variant="h6" fontWeight={500} sx={{ mb: 1.5, lineHeight: 1.4 }}>
              {fp.solution.headline}
            </Typography>

            {/* Critical insight callout */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.08),
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.4)}`,
                borderRadius: 2,
                p: 2,
                mb: 2.5,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <LightbulbIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 0.5 }}
                  >
                    {t("VisibilityStrategyPage.criticalInsightLabel")}
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                    {fp.solution.criticalInsight}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Steps */}
            <Stack spacing={2}>
              {fp.solution.steps.map((step, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    bgcolor: "background.paper",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 2.5,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Chip
                      label={i + 1}
                      size="small"
                      sx={{
                        minWidth: 28,
                        fontWeight: 500,
                        bgcolor: theme.palette.success.dark,
                        color: "white",
                      }}
                    />
                    <Box>
                      <Typography variant="body1" fontWeight={500} sx={{ mb: 0.5 }}>
                        {step.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                        {step.detail}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            {/* Do / Don't examples */}
            {(fp.solution.doExample || fp.solution.dontExample) && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={2.5}>
                {fp.solution.dontExample && (
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      bgcolor: alpha(theme.palette.error.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                      <CancelIcon sx={{ color: theme.palette.error.dark, fontSize: 16 }} />
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: theme.palette.error.dark, letterSpacing: "0.05em", textTransform: "uppercase" }}
                      >
                        {t("VisibilityStrategyPage.dontLabel")}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ lineHeight: 1.55, fontStyle: "italic" }}>
                      {fp.solution.dontExample}
                    </Typography>
                  </Paper>
                )}
                {fp.solution.doExample && (
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      bgcolor: alpha(theme.palette.success.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                      <CheckCircleIcon sx={{ color: theme.palette.success.dark, fontSize: 16 }} />
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ color: theme.palette.success.dark, letterSpacing: "0.05em", textTransform: "uppercase" }}
                      >
                        {t("VisibilityStrategyPage.doLabel")}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                      {fp.solution.doExample}
                    </Typography>
                  </Paper>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Payoff stats */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
          borderRadius: 3,
          p: 3,
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
          <TrendingUpIcon sx={{ color: theme.palette.primary.main, fontSize: 22, mt: "2px", flexShrink: 0 }} />
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}
          >
            {t("VisibilityStrategyPage.thePayoffLabel")}
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          {fp.payoff.map((p, i) => (
            <Box key={i} flex={1} textAlign={{ xs: "left", sm: "center" }}>
              <Typography variant="h2" fontWeight={900} sx={{ color: theme.palette.primary.main, letterSpacing: "-0.03em", lineHeight: 1 }}>
                {p.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {p.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Go deeper CTA — routes to the relevant product */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          borderRadius: 3,
          p: 3,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              {fp.goDeeper.headline}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {fp.goDeeper.description}
            </Typography>
          </Box>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(fp.goDeeper.productSlug)}
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              flexShrink: 0,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {fp.goDeeper.ctaLabel}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Email capture
// ---------------------------------------------------------------------------

// Lead-capture endpoint. Set this to a real form URL (e.g. a Formspree form,
// as EarlyCareerHome does) to activate signup. While it is "TODO" the card
// shows an honest "opening soon" state and never claims a subscription that
// wasn't captured (D-56).
const EMAIL_CAPTURE_ENDPOINT: string = "TODO";

export function EmailCaptureCard({
  endpoint = EMAIL_CAPTURE_ENDPOINT,
}: { endpoint?: string } = {}) {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [snackOpen, setSnackOpen] = useState(false);

  const endpointReady = endpoint !== "TODO";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    // Never fake success: only a real, successful POST marks the user subscribed.
    // Guard against a double-submit before the disabled state re-renders.
    if (status === "submitting" || !value || !value.includes("@") || !endpointReady) return;
    setStatus("submitting");
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: value, source: "visibility_workshop" }),
      });
      if (res.ok) {
        setStatus("done");
        setSnackOpen(true);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        borderRadius: 3,
        p: { xs: 4, md: 5 },
        textAlign: "center",
      }}
    >
      <EmailIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
      <Typography variant="h4" fontWeight={500} gutterBottom sx={{ letterSpacing: "-0.02em" }}>
        {t("VisibilityStrategyPage.emailCaptureHeading")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 580, mx: "auto" }}>
        {t("VisibilityStrategyPage.emailCaptureBody")}
      </Typography>
      {status === "done" ? (
        <Alert severity="success" sx={{ maxWidth: 420, mx: "auto" }}>
          {t("VisibilityStrategyPage.emailCaptureSuccessMessage")}
        </Alert>
      ) : !endpointReady ? (
        <Alert severity="info" sx={{ maxWidth: 420, mx: "auto" }}>
          {t("VisibilityStrategyPage.emailCaptureUnavailableMessage")}
        </Alert>
      ) : (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 460, mx: "auto" }}>
          {status === "error" && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("VisibilityStrategyPage.emailCaptureErrorMessage")}
            </Alert>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              size="medium"
              type="email"
              placeholder={t("VisibilityStrategyPage.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ bgcolor: "background.paper", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!email.includes("@") || status === "submitting"}
              sx={{
                bgcolor: theme.palette.primary.main,
                fontWeight: 500,
                px: 3,
                flexShrink: 0,
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
              }}
            >
              {t("VisibilityStrategyPage.subscribeButton")}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1.5 }}>
            {t("VisibilityStrategyPage.unsubscribeNote")}
          </Typography>
        </Box>
      )}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={t("VisibilityStrategyPage.subscribedSnackbar")}
      />
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function VisibilityStrategyPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("pages-account");
  const failurePoints = L(FAILURE_POINTS, i18n.language);
  const hiringRealityStats = L(HIRING_REALITY_STATS, i18n.language);
  const hiringRealityTalkingPoints = L(HIRING_REALITY_TALKING_POINTS, i18n.language);
  const weeklyActionItems = L(WEEKLY_ACTION_ITEMS, i18n.language);
  const closingNote = L(CLOSING_NOTE, i18n.language);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          py: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={2}>
            <Chip
              label={t("VisibilityStrategyPage.heroChip")}
              size="small"
              sx={{
                alignSelf: "flex-start",
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.08em",
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h2"
              fontWeight={500}
              sx={{ letterSpacing: "-0.03em", fontSize: { xs: "2.4rem", md: "3.4rem" } }}
            >
              <Trans
                i18nKey="VisibilityStrategyPage.heroHeading"
                t={t}
                components={[<br key="b0" />]}
              />
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 720, lineHeight: 1.45 }}>
              {t("VisibilityStrategyPage.heroSubheading")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.65, mt: 1 }}>
              {t("VisibilityStrategyPage.heroBody")}
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Hiring Reality */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1} mb={4}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("VisibilityStrategyPage.hiringRealityEyebrow")}
          </Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("VisibilityStrategyPage.hiringRealityHeading")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            <Trans
              i18nKey="VisibilityStrategyPage.hiringRealityBody"
              t={t}
              components={[<strong key="b0" />, <strong key="b1" />]}
            />
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={4}>
          {hiringRealityStats.map((stat, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                flex: 1,
                p: 2.5,
                textAlign: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 3,
              }}
            >
              <Typography variant="h3" fontWeight={900} sx={{ color: theme.palette.primary.main, letterSpacing: "-0.03em", lineHeight: 1 }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, lineHeight: 1.5 }}>
                {stat.label}
              </Typography>
            </Paper>
          ))}
        </Stack>

        <Paper
          elevation={0}
          sx={{
            bgcolor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            p: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight={500} gutterBottom>
            {t("VisibilityStrategyPage.whyMattersHeading")}
          </Typography>
          <Stack spacing={1} mt={1.5}>
            {hiringRealityTalkingPoints.map((p, i) => (
              <Stack key={i} direction="row" spacing={1.25} alignItems="flex-start">
                <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ lineHeight: 1.65 }}>{p}</Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      </Container>

      <Divider sx={{ mx: { xs: 2, md: 8 } }} />

      {/* Section header */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1} mb={6}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.error.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("VisibilityStrategyPage.failurePointsEyebrow")}
          </Typography>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("VisibilityStrategyPage.failurePointsHeading")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 720 }}>
            {t("VisibilityStrategyPage.failurePointsBody")}
          </Typography>
        </Stack>

        {/* Failure points */}
        {failurePoints.map((fp, i) => (
          <FailurePointSection key={fp.slug} fp={fp} index={i} />
        ))}

        <Divider sx={{ my: 6 }} />

        {/* Weekly action plan */}
        <Stack spacing={1} mb={4}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("VisibilityStrategyPage.weeklyPlanEyebrow")}
          </Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("VisibilityStrategyPage.weeklyPlanHeading")}
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={3} mb={6}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 3,
              bgcolor: alpha(theme.palette.success.main, 0.04),
              border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
              borderRadius: 3,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <CalendarTodayIcon sx={{ color: theme.palette.success.dark, fontSize: 18 }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.success.dark, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {t("VisibilityStrategyPage.thisWeekLabel")}
              </Typography>
            </Stack>
            <Stack spacing={1.25}>
              {weeklyActionItems.filter((a) => a.timeframe === "this_week").map((a, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleIcon sx={{ color: theme.palette.success.dark, fontSize: 18, mt: "2px", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{a.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              borderRadius: 3,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <TrendingUpIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {t("VisibilityStrategyPage.monthlyRhythmLabel")}
              </Typography>
            </Stack>
            <Stack spacing={1.25}>
              {weeklyActionItems.filter((a) => a.timeframe === "monthly").map((a, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: "2px", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{a.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Stack>

        {/* Closing framing */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            border: `1px dashed ${alpha(theme.palette.warning.main, 0.4)}`,
            borderRadius: 3,
            p: 3,
            mb: 6,
          }}
        >
          <Typography variant="body1" sx={{ lineHeight: 1.75, fontStyle: "italic", color: "text.primary" }}>
            {closingNote}
          </Typography>
        </Paper>

        {/* Email capture */}
        <EmailCaptureCard />

        <Divider sx={{ my: 6 }} />

        {/* Product routing index */}
        <Stack spacing={1} mb={4}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            {t("VisibilityStrategyPage.goDeeperEyebrow")}
          </Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>
            {t("VisibilityStrategyPage.goDeeperHeading")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 720 }}>
            {t("VisibilityStrategyPage.goDeeperBody")}
          </Typography>
        </Stack>

        <Stack spacing={2}>
          {failurePoints.map((fp) => (
            <Paper
              key={fp.slug}
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => navigate(fp.goDeeper.productSlug)}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.3rem",
                    flexShrink: 0,
                  }}
                >
                  {fp.icon}
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.disabled" sx={{ letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700 }}>
                    {t("VisibilityStrategyPage.failurePointShortLabel", { number: fp.number, title: fp.shortTitle })}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {fp.goDeeper.productName}
                  </Typography>
                </Box>
                <ArrowForwardIcon sx={{ color: theme.palette.primary.main }} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
